import React, { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";

export const ZEPHR_CONTENT_FEATURE = "content-hub";

interface ZephrFeatureProps {
  children: React.ReactNode;
  name?: string;
  placeholder?: React.ReactNode;
  inline?: boolean;
}

// Must stay in sync with the placeholder-reveal selector in paywall.scss.
const ZEPHR_INJECTED_TAGS = new Set(["SCRIPT", "NOSCRIPT", "STYLE"]);

// A gated container holding nothing but Zephr's injected tags was cut at the edge.
export const zephrCutDetected = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }
  return Array.from(document.querySelectorAll(".zephr-gated")).some((el) =>
    Array.from(el.children).every((child) => ZEPHR_INJECTED_TAGS.has(child.tagName))
  );
};

// Fences children in ZEPHR_FEATURE markers as static HTML injected via
// dangerouslySetInnerHTML, so an edge cut survives instead of being restored on
// hydration. renderToStaticMarkup renders without the app's providers, so
// children must not read context during render.
export const ZephrFeature = ({
  name = ZEPHR_CONTENT_FEATURE,
  children,
  placeholder,
  inline,
}: ZephrFeatureProps) => {
  const [cut] = useState(zephrCutDetected);

  // Inline children go in a <span> so the reveal CSS sees an element child.
  const html = [
    `<!-- ZEPHR_FEATURE ${name} -->`,
    cut ? "" : renderToStaticMarkup(inline ? <span>{children}</span> : <>{children}</>),
    `<!-- ZEPHR_FEATURE_END ${name} -->`,
  ].join("");

  const Wrapper = inline ? "span" : "div";

  return (
    <>
      <Wrapper
        className='zephr-gated'
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {placeholder != null && (
        <Wrapper className='zephr-placeholder' aria-hidden='true'>
          {placeholder}
        </Wrapper>
      )}
    </>
  );
};
